import type { PriceWrapper } from "../billing/prices";
import { t } from "../locale";
import { state } from "../state";
import { binarySearchInsert, binarySearchRemove } from "../utils";
import { Bulma } from "../view/bulma";
import { MaterialDesign } from "../view/material_design";

// deprecated
const subscriptionField = (
	sub: Subscription,
	prices: PriceWrapper[],
	isOwner: boolean,
): {
	element: HTMLDivElement;
	getPriceId: () => string;
	setPriceId: (id: string) => void;
} => {
	const onSelectSubscription = (): void => {
		const priceId = subSelect.getValue();
		const s =
			prices.find((price) => price.id === priceId)?.toString() || "";
		priceInfo.setValue(s);
	};

	const subSelect = Bulma.select({
		disabled: !isOwner,
		onInput: onSelectSubscription,
		options: [
			...prices.map((p) =>
				Bulma.option(p.name, p.id, sub.PriceIds.Next === p.id),
			),
		],
	});

	const priceInfo = Bulma.input({
		type: "text",
		isReadonly: true,
		styles: ["is-static"],
	});

	onSelectSubscription();
	const element = Bulma.field(
		{},
		Bulma.label(t.device.subscriptionLabel),
		Bulma.control({}, subSelect.element),
		Bulma.control({}, priceInfo.element),
	);

	return {
		element,
		getPriceId: () => subSelect.getValue(),
		setPriceId: subSelect.setValue.bind(subSelect.element),
	};
};

const sharingField = (
	meta: DeviceMetadata,
): {
	element: HTMLDivElement;
	getAccess: () => string[];
	getNotify: () => string[];
} => {
	// Create local copy to avoid changes to be reflected on devices page before
	// next auto-refresh
	const access = Array.from(meta.Access);
	const notify = Array.from(meta.Notify || []);

	const newRow = (email: string): HTMLTableRowElement => {
		const notificationsOnIcon = MaterialDesign.icon({
			id: "notifications",
			onClick: () => {
				binarySearchRemove(notify, email);
				notificationsOnIcon.replaceWith(notificationsOffIcon);
			},
		});

		const notificationsOffIcon = MaterialDesign.icon({
			id: "notifications_off",
			onClick: () => {
				binarySearchInsert(notify, email);
				notificationsOffIcon.replaceWith(notificationsOnIcon);
			},
		});

		const ownerIcon = MaterialDesign.icon({
			id: "star",
		});

		const removeUserIcon = MaterialDesign.icon({
			id: "close",
			onClick: () => {
				binarySearchRemove(access, email);
				binarySearchRemove(notify, email);
				row.remove();
			},
		});

		const row = Bulma.tableRow(
			{},
			Bulma.tableDataCell(email),
			Bulma.tableDataCell(
				notify.includes(email)
					? notificationsOnIcon
					: notificationsOffIcon,
			),
			Bulma.tableDataCell(
				email === meta.OwnerEmail ? ownerIcon : removeUserIcon,
			),
		);

		return row;
	};

	const emailInput = Bulma.input({
		type: "text",
		placeholder: t.email,
	});

	const onClickInvite = (): void => {
		const email = emailInput.getValue().trim().toLowerCase();
		emailInput.setValue("");
		if (access.includes(email)) return;

		binarySearchInsert(access, email);
		userTable.tBodies[0]
			.insertRow(access.indexOf(email))
			.replaceWith(newRow(email));
	};

	const userTable = Bulma.table({
		classList: ["is-fullwidth"],
		body: access.map((email) => newRow(email)),
	});

	const element = Bulma.field(
		{},
		Bulma.label(t.device.settingsPage.sharingLabel),
		Bulma.field(
			{
				styles: ["has-addons"],
			},
			Bulma.control(
				{
					styles: ["is-expanded"],
				},
				emailInput.element,
			),
			Bulma.control(
				{},
				Bulma.button(
					{
						styles: ["is-primary"],
						onClick: onClickInvite,
					},
					t.add,
				),
			),
		),
		userTable,
	);

	return {
		element,
		getAccess: () => [...access],
		getNotify: () => [...notify],
	};
};

const tagsField = (
	meta: DeviceMetadata,
): {
	element: HTMLDivElement;
	getTags: () => string[];
} => {
	const allTags = state.getDeviceTags().filter((tag) => !isReservedTag(tag));
	const deviceTags = meta.Tags?.filter((tag) => !isReservedTag(tag)) || [];

	const tagControl = (tag: string): HTMLDivElement => {
		const selected = deviceTags.includes(tag);

		const selectedTag = Bulma.tagWithAddon(
			{
				content: tag,
				color: "is-primary",
			},
			{
				content: MaterialDesign.icon({ id: "remove" }),
				onClick: () => {
					binarySearchRemove(deviceTags, tag);
					selectedTag.replaceWith(deselectedTag);
				},
			},
		);

		const deselectedTag = Bulma.tagWithAddon(
			{
				content: tag,
			},
			{
				content: MaterialDesign.icon({ id: "add" }),
				onClick: () => {
					binarySearchInsert(deviceTags, tag);
					deselectedTag.replaceWith(selectedTag);
				},
			},
		);

		return Bulma.control({}, selected ? selectedTag : deselectedTag);
	};

	const tagInput = Bulma.input({
		type: "text",
		placeholder: "New tag",
	});

	const onClickAdd = (): void => {
		const tag = tagInput.getValue();
		tagInput.setValue("");
		if (deviceTags.includes(tag)) return;

		binarySearchInsert(allTags, tag);
		binarySearchInsert(deviceTags, tag);

		tagsField.replaceChildren(...allTags.map((tag) => tagControl(tag)));
	};

	const tagsField = Bulma.field(
		{
			styles: ["is-grouped", "is-grouped-multiline"],
		},
		...allTags.map((tag) => tagControl(tag)),
	);

	const element = Bulma.field(
		{},
		Bulma.label(t.tags),
		Bulma.field(
			{
				styles: ["has-addons"],
			},
			Bulma.control(
				{
					styles: ["is-expanded"],
				},
				tagInput.element,
			),
			Bulma.control(
				{},
				Bulma.button(
					{
						styles: ["is-primary"],
						onClick: onClickAdd,
					},
					t.add,
				),
			),
		),
		tagsField,
	);

	return {
		element,
		getTags: () => [...deviceTags],
	};
};

/**
 * DEPRECATED
 * @param {string} name
 */
const nameField = (
	name: string,
): {
	element: HTMLDivElement;
	getName: () => string;
} => {
	const nameInput = Bulma.input({
		type: "text",
		value: name,
	});

	const element = Bulma.field(
		{},
		Bulma.label(t.name),
		Bulma.control({}, nameInput.element),
	);

	return {
		element,
		getName: () => nameInput.getValue(),
	};
};

const ownerField = (
	meta: DeviceMetadata,
	isOwner: boolean,
): {
	element: HTMLDivElement;
	getOwner: () => string;
} => {
	const ownerSelect = Bulma.select({
		disabled: !isOwner,
		options: meta.Access.map((email) =>
			Bulma.option(email, email, email === meta.OwnerEmail),
		),
	});

	const element = Bulma.field(
		{},
		Bulma.label(t.owner),
		Bulma.control({}, ownerSelect.element),
	);

	return {
		element,
		getOwner: () => ownerSelect.getValue(),
	};
};

/**
 * @param {string} tag
 * @returns {boolean}
 */
const isReservedTag = (tag: string): boolean => {
	const productTags = [
		"FarmCam SIM",
		"FenceAlarm",
		"FenceAlarm2",
		"SmartPlug",
		"SmartPlug2",
	];
	if (productTags.includes(tag)) return true;
	return /.+@.+\..+/.test(tag);
};

export { nameField, ownerField, sharingField, subscriptionField, tagsField };
